import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import CloudIcon from '@mui/icons-material/Cloud';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import SecurityIcon from '@mui/icons-material/Security';
import GroupsIcon from '@mui/icons-material/Groups';
import premium1 from '../assets/premium1.png';

const Branchs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const features = [
    {
      title: 'AI-Powered Fault Detection',
      description: 'Advanced AI algorithms for instant device diagnostics',
      icon: <SmartphoneIcon sx={{ fontSize: 40, color: '#FFD700' }} />,
      details: 'Our cutting-edge AI system analyzes device issues in real-time'
    },
    {
      title: 'Cloud Service History',
      description: 'Access your repair records anytime, anywhere',
      icon: <CloudIcon sx={{ fontSize: 40, color: '#FFD700' }} />,
      details: 'Secure cloud storage for all your device maintenance history'
    },
    {
      title: 'Genuine Parts Marketplace',
      description: 'Direct access to verified spare parts',
      icon: <StorefrontIcon sx={{ fontSize: 40, color: '#FFD700' }} />,
      details: 'Connect with trusted vendors for authentic components'
    },
    {
      title: '24/7 Smart Support',
      description: 'AI-powered assistance round the clock',
      icon: <SupportAgentIcon sx={{ fontSize: 40, color: '#FFD700' }} />,
      details: 'Instant solutions through our intelligent support system'
    }
  ];

  return (
    <Box sx={{ bgcolor: '#000000', minHeight: '100vh', color: '#FFFFFF' }}>
      {/* Hero Section */}
      <Box 
        sx={{ 
          pt: { xs: 10, md: 15 },
          pb: { xs: 8, md: 12 },
          background: 'linear-gradient(45deg, #000000 30%, #1A1A1A 90%)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  fontWeight: 800,
                  mb: 2,
                  background: 'linear-gradient(45deg, #FFD700 30%, #FFC107 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  position: 'relative',
                  zIndex: 2
                }}
              >
                Smart Repairs,
                <br />
                Powered by AI
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 4,
                  color: '#CCCCCC',
                  fontWeight: 300,
                  lineHeight: 1.6,
                  position: 'relative',
                  zIndex: 2
                }}
              >
                Transform your device repair experience with our AI-driven solutions. 
                Faster diagnostics, smarter repairs.
              </Typography>
              <Button 
                variant="contained" 
                size="large"
                startIcon={<RocketLaunchIcon />}
                sx={{
                  bgcolor: '#FFD700',
                  color: '#000000',
                  px: 4,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  position: 'relative',
                  zIndex: 2,
                  '&:hover': {
                    bgcolor: '#FFC107',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 5px 15px rgba(255,215,0,0.3)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Explore Solutions
              </Button>
            </Grid>
            
            {/* Hero Image Section */}
            <Grid item xs={12} md={6} sx={{ position: 'relative' }}>
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 2,
                  '@keyframes float': {
                    '0%, 100%': {
                      transform: 'translateY(0px) rotate(0deg)',
                    },
                    '50%': {
                      transform: 'translateY(-20px) rotate(2deg)',
                    },
                  },
                  animation: 'float 6s ease-in-out infinite',
                  display: { xs: 'none', md: 'block' }, // Hide on mobile
                }}
              >
                <Box
                  component="img"
                  src={premium1}
                  alt="Premium Service"
                  sx={{
                    width: '100%',
                    maxWidth: '600px',
                    height: 'auto',
                    filter: 'drop-shadow(0 10px 20px rgba(255,215,0,0.2))',
                  }}
                />
                {/* Decorative Elements */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '10%',
                    right: '10%',
                    width: '100px',
                    height: '100px',
                    background: 'radial-gradient(circle, rgba(255,215,0,0.1) 0%, rgba(255,215,0,0) 70%)',
                    borderRadius: '50%',
                    animation: 'pulse 3s ease-in-out infinite',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '20%',
                    left: '10%',
                    width: '150px',
                    height: '150px',
                    background: 'radial-gradient(circle, rgba(255,215,0,0.05) 0%, rgba(255,215,0,0) 70%)',
                    borderRadius: '50%',
                    animation: 'pulse 4s ease-in-out infinite',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
        
        {/* Background Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '50%',
            height: '100%',
            background: 'radial-gradient(circle at top right, rgba(255,215,0,0.05) 0%, rgba(0,0,0,0) 70%)',
            zIndex: 1
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '50%',
            height: '50%',
            background: 'radial-gradient(circle at bottom left, rgba(255,215,0,0.03) 0%, rgba(0,0,0,0) 70%)',
            zIndex: 1
          }}
        />
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card 
                sx={{ 
                  bgcolor: '#1A1A1A',
                  color: '#FFFFFF',
                  height: '100%',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    bgcolor: '#222222'
                  },
                  border: '1px solid #333333'
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ mb: 3 }}>
                    {feature.icon}
                  </Box>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      mb: 2,
                      fontWeight: 700,
                      color: '#FFD700'
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography 
                    sx={{ 
                      mb: 2,
                      color: '#CCCCCC'
                    }}
                  >
                    {feature.description}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#999999',
                      fontSize: '0.9rem'
                    }}
                  >
                    {feature.details}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Statistics Section */}
        <Box sx={{ mt: 12, mb: 8 }}>
          <Grid container spacing={4} justifyContent="center">
            {[
              { number: '24/7', label: 'AI Support' },
              { number: '99%', label: 'Accuracy Rate' },
              { number: '1000+', label: 'Daily Repairs' },
              { number: '50K+', label: 'Happy Users' }
            ].map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box 
                  sx={{ 
                    textAlign: 'center',
                    p: 3,
                    border: '1px solid #333333',
                    bgcolor: '#1A1A1A'
                  }}
                >
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      color: '#FFD700',
                      fontWeight: 700,
                      mb: 1
                    }}
                  >
                    {stat.number}
                  </Typography>
                  <Typography 
                    sx={{ 
                      color: '#CCCCCC',
                      textTransform: 'uppercase',
                      letterSpacing: 1
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box 
          sx={{ 
            mt: 12,
            p: 6,
            textAlign: 'center',
            background: 'linear-gradient(45deg, #1A1A1A 30%, #222222 90%)',
            border: '1px solid #333333'
          }}
        >
          <Typography 
            variant="h3" 
            sx={{ 
              mb: 3,
              fontWeight: 700,
              color: '#FFD700'
            }}
          >
            Ready to Get Started?
          </Typography>
          <Typography 
            sx={{ 
              mb: 4,
              color: '#CCCCCC',
              maxWidth: '600px',
              mx: 'auto'
            }}
          >
            Join thousands of satisfied users who have transformed their repair experience with our AI-powered platform.
          </Typography>
          <Button 
            variant="contained"
            size="large"
            sx={{
              bgcolor: '#FFD700',
              color: '#000000',
              px: 6,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#FFC107'
              }
            }}
          >
            Start Now
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Branchs;